import React, { Suspense, lazy, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import { IconPlus } from '@tabler/icons-react';

import AdminLayout from '../../../layouts/AdminLayout';
import ErrorBoundary from '../../../shared/components/errors/ErrorBoundary';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import useIndexBillingConnections from '../../../services/billing-integration/hooks/useIndexBillingConnections';
import { useDeleteBillingConnection } from '../../../services/billing-integration/hooks/useDeleteBillingConnection';
import { BillingConnectionData } from '../../../services/billing-integration/response/BillingConnectionResponse';
import BillingConnectionTable from '../components/BillingConnectionTable';
import BillingEndpointInfo from '../components/BillingEndpointInfo';
import { getApiErrorMessage } from '../utils/apiError';

const CreateBillingConnectionDialog = lazy(() => import('../components/CreateBillingConnectionDialog'));
const UpdateBillingConnectionDialog = lazy(() => import('../components/UpdateBillingConnectionDialog'));

const DELETE_TOAST_ID = 'delete-billing-connection';

/**
 * Settings > Integrations. Currently hosts one card: "Billing connection"
 * (links this PMS to the central Billing system). Dev-only (see AppRouterOld.tsx).
 */
const IntegrationsPage: React.FC = () => {
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingConnection, setEditingConnection] = useState<BillingConnectionData | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [page, setPage] = useState(1);

    const { data, isLoading, isError, refetch } = useIndexBillingConnections({ page });
    const { mutate: deleteConnection } = useDeleteBillingConnection();

    const connections = data?.data ?? [];
    const pagination = data?.pagination;

    // Deleting the last row of the last page leaves that page empty — step back.
    useEffect(() => {
        if (pagination && pagination.last_page >= 1 && page > pagination.last_page) {
            setPage(pagination.last_page);
        }
    }, [pagination, page]);

    const handleDelete = (connection: BillingConnectionData) => {
        Swal.fire({
            title: 'Delete connection?',
            text: `"${connection.name || connection.code}" will be removed and the Billing app will no longer be able to pull usage with it. This cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
        }).then((result) => {
            if (!result.isConfirmed) return;
            setDeletingId(connection.id);
            toast.loading('Deleting billing connection...', { id: DELETE_TOAST_ID });
            deleteConnection({ id: connection.id }, {
                onSuccess: () => {
                    toast.success('Billing connection deleted successfully', { id: DELETE_TOAST_ID });
                },
                onError: (error) => {
                    toast.error(getApiErrorMessage(error, 'Failed to delete billing connection'), { id: DELETE_TOAST_ID });
                },
                onSettled: () => setDeletingId(null),
            });
        });
    };

    // Keep the dialog in sync with refreshed list data (e.g. after replacing the key).
    const editingFresh = editingConnection
        ? connections.find((c) => c.id === editingConnection.id) ?? editingConnection
        : null;

    return (
        <AdminLayout>
            <ErrorBoundary fallback={
                <div className="text-center p-5">
                    <h2>An Error Occurred</h2>
                    <p>Could not display the integrations page.</p>
                </div>
            }>
                <div className="container-fluid mt-5">
                    <h3 className="mb-3">Integrations</h3>

                    <div className="card">
                        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <div>
                                <h4 className="card-title mb-0">Billing connection</h4>
                                <div className="small text-muted">
                                    Links this PMS to the central Billing system so it can pull usage per company (B2B) or per property (B2C).
                                </div>
                            </div>
                            <button type="button" className="btn btn-primary" onClick={() => setShowCreateDialog(true)}>
                                <IconPlus size={16} className="me-1" /> Add connection
                            </button>
                        </div>
                        <div className="card-body">
                            <BillingEndpointInfo />
                            <BillingConnectionTable
                                connections={connections}
                                isLoading={isLoading}
                                isError={isError}
                                onRetry={() => refetch()}
                                onEdit={setEditingConnection}
                                onDelete={handleDelete}
                                deletingId={deletingId}
                            />
                            {pagination && pagination.last_page > 1 && (
                                <PaginationComponent
                                    totalPages={pagination.last_page}
                                    currentPage={pagination.current_page}
                                    onPageChange={setPage}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </ErrorBoundary>

            <Suspense fallback={null}>
                {showCreateDialog && (
                    <CreateBillingConnectionDialog show={showCreateDialog} onClose={() => setShowCreateDialog(false)} />
                )}
                {editingFresh && (
                    <UpdateBillingConnectionDialog
                        show={!!editingFresh}
                        connection={editingFresh}
                        onClose={() => setEditingConnection(null)}
                    />
                )}
            </Suspense>
        </AdminLayout>
    );
};

export default IntegrationsPage;
